import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlignLeft, AlignRight, Briefcase, ImagePlus, RotateCcw, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_LINES = 4;
const LOGO_SIZES = ['small', 'medium', 'large'];
// The preview box stands for the PDF content width (A4 minus margins); all header sizes are in the same mm.
const CONTENT_MM = 184;
const LOGO_MM = { small: 8, medium: 12, large: 18 };
const LOGO_MAX_W_MM = 80;
const TEXT_MAX_W_MM = 110;
const cq = (mm) => `${((mm / CONTENT_MM) * 100).toFixed(3)}cqw`;
const DEFAULT_ACCENT = '#AA0000';
const ACCENT_PRESETS = ['#AA0000', '#1F2937', '#1D4ED8', '#0F766E', '#15803D', '#B45309', '#7C3AED', '#BE185D'];

/** Dividing-line colour: presets plus a custom colour. */
const AccentPicker = ({ value, onChange }) => {
  const { t } = useTranslation();
  const current = String(value || DEFAULT_ACCENT).toUpperCase();
  const custom = !ACCENT_PRESETS.includes(current);
  return (
    <div className="space-y-1.5" data-testid="emp-header-accent">
      <Label className="text-xs">{t('empHeader.accent')}</Label>
      <div className="flex flex-wrap items-center gap-2">
        {ACCENT_PRESETS.map((c) => (
          <button key={c} type="button" onClick={() => onChange(c)} title={c}
            aria-label={c} aria-pressed={current === c}
            className={`h-8 w-8 sm:h-6 sm:w-6 rounded-full border-2 transition-transform hover:scale-110 ${current === c ? 'border-slate-900 ring-2 ring-offset-1 ring-slate-400' : 'border-white shadow'}`}
            style={{ backgroundColor: c }} data-testid={`emp-header-accent-${c.slice(1).toLowerCase()}`} />
        ))}
        <label className={`relative flex h-8 sm:h-6 items-center gap-1 rounded-full border px-2 text-xs cursor-pointer ${custom ? 'border-slate-900' : 'border-slate-300'}`}>
          <span className="h-4 w-4 rounded-full border" style={{ backgroundColor: current }} />
          {t('empHeader.customColour')}
          <input type="color" value={current.toLowerCase()} onChange={(e) => onChange(e.target.value.toUpperCase())}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0" data-testid="emp-header-accent-custom" />
        </label>
        <span className="text-[11px] font-mono text-slate-500" data-testid="emp-header-accent-value">{current}</span>
      </div>
    </div>
  );
};

/** Profile business details -> header lines (name, street, postcode + city, NIP). */
const businessLines = ({ business_name: name, business_address: addr, tax_id: nip }) => {
  let addrLines = String(addr || '').split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
  if (addrLines.length === 1 && addrLines[0].includes(', ')) {
    const i = addrLines[0].lastIndexOf(', ');
    addrLines = [addrLines[0].slice(0, i), addrLines[0].slice(i + 2)];
  }
  return [name, ...addrLines, nip ? `NIP: ${nip}` : ''].map((x) => String(x || '').trim()).filter(Boolean);
};

/** Small to-scale look at the top of the PDF (same layout as the generated masthead). */
const HeaderPreview = ({ logoUrl, lines, size, position, accent = DEFAULT_ACCENT, bare = false, testId = 'emp-header-preview' }) => {
  const { t } = useTranslation();
  const [aspect, setAspect] = useState(0);
  const text = lines.filter((x) => x.trim());
  const right = position === 'right';
  // Same box maths as the PDF: chosen height, shrunk proportionally if wider than the max width.
  const h = LOGO_MM[size] || LOGO_MM.medium;
  const a = aspect || 1;
  const wMm = Math.min(h * a, LOGO_MAX_W_MM);
  const logoStyle = { width: cq(wMm), height: cq(wMm / a), display: 'block' };
  return (
    <div className="rounded-md border bg-white p-3 shadow-sm" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">{t(bare ? 'empHeader.title' : 'empHeader.preview')}</div>
      <div style={{ containerType: 'inline-size' }}>
      {logoUrl || text.length ? (
        <div className={`flex items-start border-b-2 pb-2 ${right ? 'flex-row-reverse' : ''} ${logoUrl ? 'justify-between' : (right ? 'justify-start' : 'justify-end')}`} style={{ borderBottomColor: accent }} data-testid="emp-header-preview-row" data-position={right ? 'right' : 'left'}>
          {logoUrl && (
            <div className="shrink-0">
              <img src={logoUrl} alt="" style={logoStyle} data-testid="emp-header-preview-logo"
                onLoad={(e) => setAspect(e.currentTarget.naturalWidth / Math.max(1, e.currentTarget.naturalHeight))} />
            </div>
          )}
          <div
            className={`${right ? 'text-left' : 'text-right'} leading-snug text-[#555] min-w-0 flex-auto`}
            style={{ maxWidth: cq(TEXT_MAX_W_MM), fontSize: `max(7px, ${cq(2.12)})`, overflowWrap: 'anywhere', [right ? 'marginRight' : 'marginLeft']: logoUrl ? cq(6) : 0 }}
            data-testid="emp-header-preview-lines"
          >
            {text.map((x, i) => <div key={i}>{x}</div>)}
          </div>
        </div>
      ) : (
        <div className="text-xs text-slate-400 italic border-b border-dashed pb-2">{t('empHeader.previewEmpty')}</div>
      )}
      </div>
      {!bare && (<>
      <div className="mt-2 flex items-baseline justify-between">
        <span className="text-[13px] font-bold text-[#AA0000]">Faktura / Invoice</span>
        <span className="h-1.5 w-16 rounded bg-slate-100" />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-3">
        <div className="space-y-1"><div className="h-1.5 w-3/4 rounded bg-slate-100" /><div className="h-1.5 w-1/2 rounded bg-slate-100" /></div>
        <div className="space-y-1"><div className="h-1.5 w-3/4 rounded bg-slate-100" /><div className="h-1.5 w-2/3 rounded bg-slate-100" /></div>
      </div>
      </>)}
    </div>
  );
};

const useLogoPreview = (logoId) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!logoId) { setUrl(''); return undefined; }
    let objectUrl = '';
    let alive = true;
    axios.get(`${API}/api/employee-invoices/header-logo/${logoId}`, { responseType: 'blob' })
      .then((r) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      })
      .catch(() => { if (alive) setUrl(''); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [logoId]);
  return url;
};

/** Optional PDF header for an employee invoice; the last one is remembered per employee. */
export const InvoiceHeaderDisplay = ({ invoice }) => {
  const logoUrl = useLogoPreview(invoice.header_enabled ? invoice.header_logo_id : null);
  if (!invoice.header_enabled || !((invoice.header_lines || []).length || invoice.header_logo_id)) return null;
  return (
    <HeaderPreview bare testId="emp-view-header" logoUrl={logoUrl} lines={invoice.header_lines || []}
      size={invoice.header_logo_size || 'medium'} position={invoice.header_logo_position || 'left'} accent={invoice.header_accent_color || DEFAULT_ACCENT} />
  );
};

export const InvoiceHeaderEditor = ({ form, setForm, employeeId }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const logoUrl = useLogoPreview(form.header_logo_id);
  const lines = [...(form.header_lines || []), '', '', '', ''].slice(0, MAX_LINES);

  const loadSaved = async (silent) => {
    try {
      const r = await axios.get(`${API}/api/employee-invoices/header-defaults`, { params: employeeId ? { employee_id: employeeId } : {} });
      if (!r.data.lines.length && !r.data.logo_id) { if (!silent) toast.info(t('empHeader.noneSaved')); return; }
      setForm((p) => ({ ...p, header_lines: r.data.lines, header_logo_id: r.data.logo_id || null, header_logo_size: r.data.logo_size || 'medium', header_logo_position: r.data.logo_position || 'left', header_accent_color: r.data.accent_color || DEFAULT_ACCENT }));
    } catch { if (!silent) toast.info(t('empHeader.noneSaved')); }
  };

  const fillFromBusiness = async () => {
    try {
      const r = await axios.get(`${API}/api/employee-invoices/header-business-details`, { params: employeeId ? { employee_id: employeeId } : {} });
      const current = [...(form.header_lines || []), '', '', '', ''].slice(0, MAX_LINES);
      const todo = businessLines(r.data).filter((x) => !current.some((c) => c.trim() === x));
      if (!businessLines(r.data).length) { toast.info(t('empHeader.noBusiness')); return; }
      if (!todo.length) { toast.info(t('empHeader.alreadyFilled')); return; }
      const next = current.map((c) => (c.trim() ? c : (todo.shift() || '')));
      setForm((p) => ({ ...p, header_lines: next }));
      if (todo.length) toast.info(t('empHeader.noRoom', { count: todo.length }));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('empHeader.noBusiness'));
    }
  };

  const toggle = (on) => {
    setForm((p) => ({ ...p, header_enabled: on }));
    if (on && !(form.header_lines || []).some(Boolean) && !form.header_logo_id) loadSaved(true);
  };

  const setLine = (i, v) => setForm((p) => {
    const next = [...(p.header_lines || []), '', '', '', ''].slice(0, MAX_LINES);
    next[i] = v;
    return { ...p, header_lines: next };
  });

  const upload = async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/api/employee-invoices/header-logo`, fd);
      setForm((p) => ({ ...p, header_logo_id: r.data.logo_id }));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('empHeader.uploadFailed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="p-3 border rounded-lg space-y-3" data-testid="emp-header-editor">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-medium text-slate-600">{t('empHeader.title')}</h4>
          <p className="text-xs text-slate-500">{t('empHeader.hint')}</p>
        </div>
        <Switch checked={!!form.header_enabled} onCheckedChange={toggle} data-testid="emp-header-toggle" />
      </div>
      {form.header_enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-4">
          <div className="space-y-2">
            <Label className="text-xs">{t('empHeader.logo')}</Label>
            <div className="h-20 border border-dashed rounded-md flex items-center justify-center bg-slate-50 overflow-hidden">
              {logoUrl
                ? <img src={logoUrl} alt="" className="max-h-full max-w-full object-contain" data-testid="emp-header-logo-preview" />
                : <span className="text-xs text-slate-400">{t('empHeader.noLogo')}</span>}
            </div>
            <div className="flex rounded-md border overflow-hidden text-xs" role="group" aria-label={t('empHeader.logoSize')} data-testid="emp-header-logo-size">
              {LOGO_SIZES.map((s) => (
                <button key={s} type="button"
                  onClick={() => setForm((p) => ({ ...p, header_logo_size: s }))}
                  aria-pressed={(form.header_logo_size || 'medium') === s}
                  className={`flex-1 px-2 py-2 sm:py-1 transition-colors ${(form.header_logo_size || 'medium') === s ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                  data-testid={`emp-header-logo-size-${s}`}>
                  {t(`empHeader.size_${s}`)}
                </button>
              ))}
            </div>
            <div className="flex rounded-md border overflow-hidden text-xs" role="group" aria-label={t('empHeader.logoPosition')} data-testid="emp-header-logo-position">
              {['left', 'right'].map((pos) => (
                <button key={pos} type="button"
                  onClick={() => setForm((p) => ({ ...p, header_logo_position: pos }))}
                  aria-pressed={(form.header_logo_position || 'left') === pos}
                  className={`flex flex-1 items-center justify-center gap-1 px-2 py-2 sm:py-1 transition-colors ${(form.header_logo_position || 'left') === pos ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                  data-testid={`emp-header-logo-position-${pos}`}>
                  {pos === 'left' ? <AlignLeft className="w-3 h-3" /> : <AlignRight className="w-3 h-3" />}
                  {t(`empHeader.pos_${pos}`)}
                </button>
              ))}
            </div>

            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
              disabled={uploading} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} data-testid="emp-header-logo-input" />
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="emp-header-logo-upload">
                <ImagePlus className="w-3.5 h-3.5 mr-1" />{uploading ? t('empHeader.uploading') : t('empHeader.upload')}
              </Button>
              {form.header_logo_id && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setForm((p) => ({ ...p, header_logo_id: null }))} data-testid="emp-header-logo-remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">{t('empHeader.lines')}</Label>
            {lines.map((v, i) => (
              <Input key={i} value={v} maxLength={200} onChange={(e) => setLine(i, e.target.value)}
                placeholder={t(`empHeader.linePh${i + 1}`)} className="h-8 text-sm" data-testid={`emp-header-line-${i}`} />
            ))}
            <div className="flex flex-wrap gap-1">
              <Button type="button" size="sm" variant="ghost" className="text-xs" onClick={fillFromBusiness} data-testid="emp-header-fill-business">
                <Briefcase className="w-3.5 h-3.5 mr-1" />{t('empHeader.fillBusiness')}
              </Button>
              <Button type="button" size="sm" variant="ghost" className="text-xs" onClick={() => loadSaved(false)} data-testid="emp-header-load-saved">
                <RotateCcw className="w-3.5 h-3.5 mr-1" />{t('empHeader.useSaved')}
              </Button>
            </div>
          </div>
          <div className="sm:col-span-2">
            <AccentPicker value={form.header_accent_color || DEFAULT_ACCENT}
              onChange={(c) => setForm((p) => ({ ...p, header_accent_color: c }))} />
          </div>
          <div className="sm:col-span-2">
            <HeaderPreview logoUrl={logoUrl} lines={lines} size={form.header_logo_size || 'medium'} position={form.header_logo_position || 'left'} accent={form.header_accent_color || DEFAULT_ACCENT} />
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceHeaderEditor;
